import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, Check, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import Logo from "@/components/Logo";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password reset — sign in with your new password.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed — request a new link");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#08080A] text-[#F4F4F5] grain flex flex-col">
      <header className="px-6 h-[72px] flex items-center justify-between max-w-7xl mx-auto w-full">
        <Link to="/" data-testid="nav-logo"><Logo /></Link>
        <Link to="/login" className="btn-ghost-gold flex items-center gap-1.5 text-xs font-sub px-4 py-2 rounded-full">
          <ArrowLeft className="w-3.5 h-3.5" /> Sign In
        </Link>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md bg-[#101014] border border-[rgba(212,175,55,0.3)] rounded-2xl p-8 sm:p-10"
          style={{ boxShadow: "0 0 80px rgba(212,175,55,0.08)" }}
        >
          {done ? (
            <div className="text-center">
              <div className="w-14 h-14 mx-auto rounded-full emblem-coin flex items-center justify-center mb-6">
                <Check className="w-6 h-6 text-[#0A0A0C]" strokeWidth={3} />
              </div>
              <h1 className="font-display font-bold text-2xl mb-3" data-testid="reset-success-text">Password updated</h1>
              <p className="text-sm text-zinc-400 mb-8">You're back in control. Sign in with your new password.</p>
              <button
                data-testid="reset-login-btn"
                onClick={() => navigate("/login")}
                className="btn-gold w-full font-sub text-sm py-4 rounded-full"
              >
                Sign In
              </button>
            </div>
          ) : !token ? (
            <div className="text-center">
              <h1 className="font-display font-bold text-2xl mb-3" data-testid="reset-invalid-text">Invalid link</h1>
              <p className="text-sm text-zinc-400 mb-8">This reset link is missing its token. Request a fresh one from the sign-in page.</p>
              <button
                data-testid="reset-back-btn"
                onClick={() => navigate("/login")}
                className="btn-ghost-gold w-full font-sub font-semibold text-sm py-4 rounded-full"
              >
                Back to Sign In
              </button>
            </div>
          ) : (
            <>
              <p className="mono text-[10px] tracking-[0.3em] uppercase text-[#8A7322] text-center mb-2">Account Recovery</p>
              <h1 className="font-display font-bold text-2xl text-center mb-8">Set a new password</h1>
              <form onSubmit={submit} className="space-y-4">
                <div className="relative">
                  <Lock className="w-4 h-4 text-zinc-600 absolute left-4 top-1/2 -translate-y-1/2" />
                  <input
                    data-testid="reset-password-input"
                    type="password"
                    required
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="New password (min 6 chars)"
                    className="w-full bg-[#08080A] border border-[#26262F] rounded-full pl-11 pr-4 py-3.5 text-sm outline-none focus:border-[#D4AF37] transition-colors placeholder:text-zinc-600"
                  />
                </div>
                <div className="relative">
                  <Lock className="w-4 h-4 text-zinc-600 absolute left-4 top-1/2 -translate-y-1/2" />
                  <input
                    data-testid="reset-confirm-input"
                    type="password"
                    required
                    minLength={6}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Confirm new password"
                    className="w-full bg-[#08080A] border border-[#26262F] rounded-full pl-11 pr-4 py-3.5 text-sm outline-none focus:border-[#D4AF37] transition-colors placeholder:text-zinc-600"
                  />
                </div>
                <button
                  data-testid="reset-submit-btn"
                  type="submit"
                  disabled={busy}
                  className="btn-gold w-full font-sub text-sm py-4 rounded-full disabled:opacity-60"
                >
                  {busy ? "Working..." : "Reset Password"}
                </button>
              </form>
              <p className="text-center text-[11px] text-zinc-600 mt-6">This link expires 30 minutes after it was sent.</p>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
